
import React, { useState, useEffect } from 'react';
import { Sentry } from '@/lib/sentry';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, X, Eye, FileText, CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import EstimatePreview from './EstimatePreview';
import ChangeOrderPreview from './ChangeOrderPreview';
import EstimatePdfStylePreview from './preview/EstimatePdfStylePreview';
import { ChangeOrderExportData } from '@/utils/changeOrderExportUtils';
import { supabase } from '@/integrations/supabase/client';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

interface EstimatePreviewModalProps {
  estimateId: string;
  versionId?: string;
  isOpen: boolean;
  onClose: () => void;
}

type PreviewMode = 'regular' | 'pdf-style';

const EstimatePreviewModal: React.FC<EstimatePreviewModalProps> = ({
  estimateId,
  versionId,
  isOpen,
  onClose,
}) => {
  const [data, setData] = useState<ChangeOrderExportData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('pdf-style');

  useEffect(() => {
    if (isOpen && estimateId) {
      loadEstimateData();
    }
  }, [isOpen, estimateId, versionId]);

  const loadEstimateData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('[EstimatePreviewModal] Loading estimate data for ID:', estimateId, 'Version ID:', versionId);
      
      // FIXED: Determine correct data fetching strategy
      let exportData;
      
      if (!versionId) {
        // No version specified - use live data for best categorization
        console.log('[EstimatePreviewModal] No version ID - using live estimate data');
        const { fetchEstimateExportData } = await import('@/utils/estimateExportUtils');
        exportData = await fetchEstimateExportData(estimateId);
      } else {
        // Version specified - check if it's a change order or regular estimate version
        const { data: version } = await supabase
          .from('estimate_versions')
          .select('version_type, status')
          .eq('id', versionId)
          .single();
          
        if (version?.version_type === 'change_order') {
          console.log('[EstimatePreviewModal] Loading change order data');
          const { fetchChangeOrderExportData } = await import('@/utils/changeOrderExportUtils');
          exportData = await fetchChangeOrderExportData(estimateId, versionId);
        } else {
          console.log('[EstimatePreviewModal] Loading versioned estimate data');
          const { fetchEstimateExportData } = await import('@/utils/estimateExportUtils');
          exportData = await fetchEstimateExportData(estimateId, versionId);
        }
      }
      
      console.log('[EstimatePreviewModal] Data loaded successfully:', exportData);
      setData(exportData);
    } catch (error) {
      console.error('[EstimatePreviewModal] Error loading estimate data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to load estimate data: ${errorMessage}`);
      toast.error('Failed to load estimate data');
    } finally {
      setIsLoading(false);
    }
  };

  const generatePdfContent = async () => {
    if (!data) {
      toast.error('No data available');
      return null;
    }

    try {
      // FIXED: Use the correct DOM ID based on data type
      const previewElementId = data.isChangeOrder ? 'change-order-preview' : 'estimate-preview';
      const previewElement = document.getElementById(previewElementId);
      
      console.log('[EstimatePreviewModal] Looking for element with ID:', previewElementId);
      console.log('[EstimatePreviewModal] Found element:', !!previewElement);
      console.log('[EstimatePreviewModal] Data type:', { isChangeOrder: data.isChangeOrder });
      
      if (!previewElement) {
        console.error('[EstimatePreviewModal] Preview element not found with ID:', previewElementId);
        toast.error('Preview content not found');
        return null;
      }

      const htmlContent = previewElement.innerHTML;

      // Call the edge function to prepare PDF content
      const { data: response, error } = await supabase.functions.invoke('generate-estimate-pdf', {
        body: {
          htmlContent,
          estimateData: data
        }
      });

      if (error) {
        throw new Error(error.message || 'PDF generation failed');
      }

      return response;

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
      return null;
    }
  };

  const handleDownloadPDF = async () => {
    setIsPdfLoading(true);
    try {
      const pdfResponse = await generatePdfContent();
      
      if (pdfResponse?.htmlContent) {
        const { downloadPdfFromHtml } = await import('@/utils/downloadPdfFromHtml');
        const filename = data?.isChangeOrder
          ? `change-order-${data?.estimate?.estimate_number || estimateId}`
          : `estimate-${data?.estimate?.estimate_number || estimateId}`;
        
        await downloadPdfFromHtml(pdfResponse.htmlContent, filename);
        toast.success('PDF downloaded successfully');
      }
    } catch (error) {
      console.error('[EstimatePreviewModal] PDF download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setIsPdfLoading(false);
    }
  };

  const getPreviewTitle = () => {
    if (data?.isChangeOrder) {
      return 'Change Order Preview';
    }
    return 'Estimate Preview';
  };

  return (
    <Sentry.ErrorBoundary fallback={<div className="p-8 text-center text-destructive">Something went wrong loading the estimate preview.</div>}>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden p-0 [&>button]:hidden">
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <DialogTitle className="text-base">{getPreviewTitle()}</DialogTitle>
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Estimate Date Picker */}
              {data?.estimate && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className={cn("justify-start text-left font-normal h-8", !(data.estimate.estimate_date || data.estimate.created_at) && "text-muted-foreground")}>
                      <CalendarIcon className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline text-xs mr-1">Estimate Date:</span>
                      <span className="hidden sm:inline">{format(new Date(data.estimate.estimate_date || data.estimate.created_at), 'dd MMMM yyyy')}</span>
                      <span className="sm:hidden">{format(new Date(data.estimate.estimate_date || data.estimate.created_at), 'dd/MM')}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={new Date(data.estimate.estimate_date || data.estimate.created_at)}
                      onSelect={async (date) => {
                        if (date) {
                          const { error } = await supabase
                            .from('estimates')
                            .update({ estimate_date: date.toISOString() } as any)
                            .eq('id', estimateId);
                          if (error) {
                            toast.error('Failed to update date');
                          } else {
                            toast.success('Date updated');
                            loadEstimateData();
                          }
                        }
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              )}
              {/* Preview Mode Toggle */}
              <div className="flex items-center gap-1 bg-muted rounded-md p-1">
                <Button
                  variant={previewMode === 'regular' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('regular')}
                  className="h-8 px-2 sm:px-3"
                >
                  <Eye className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Regular</span>
                </Button>
                <Button
                  variant={previewMode === 'pdf-style' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('pdf-style')}
                  className="h-8 px-2 sm:px-3"
                >
                  <FileText className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">PDF Style</span>
                </Button>
              </div>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownloadPDF}
                disabled={!data || isLoading || isPdfLoading}
                className="h-8"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{isPdfLoading ? 'Generating...' : 'Save as PDF'}</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-8">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div className="overflow-auto max-h-[calc(90vh-80px)]">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <p>Loading preview...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center p-8">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={loadEstimateData} variant="outline">
                Try Again
              </Button>
            </div>
          ) : data ? (
            <>
              {/* PDF generation captures from the visible EstimatePdfStylePreview via id="estimate-preview" */}
              
              {/* Always render PDF-style for capture; hide when in regular mode */}
              <div style={previewMode !== 'pdf-style' ? { position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' } : {}}>
                <EstimatePdfStylePreview data={data} />
              </div>

              {/* Show regular preview when in regular mode */}
              {previewMode === 'regular' && (
                data.isChangeOrder ? (
                  <ChangeOrderPreview data={data as ChangeOrderExportData} />
                ) : (
                  <EstimatePreview data={data} />
                )
              )}
            </>
          ) : (
            <div className="flex items-center justify-center p-8">
              <p>No data available</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </Sentry.ErrorBoundary>
  );
};

export default EstimatePreviewModal;
